/* Передача итога в другой экран студии с хвоста движка (engine-kit, план M3 §2 S4): «Сделать рилс», «Оживить», «Сделать кружок»,
   переходы «Что дальше». Перед передачей заново сверяются владелец (GET /app/me — тот, чей заказ на экране) и заказ (GET /app/orders/{id}:
   готов, результат доступен, та же ревизия, что на экране); файл — авторизованная выгрузка результата сервера или уже загруженный Blob
   того же заказа и ревизии (кадры галереи) того типа, что нужен. Затем файл встаёт в память вкладки (stagePhotoResult / stageVideoResult /
   stageVoiceResult — в хранилище не пишутся ни файлы, ни адреса) и студия переходит к экрану (openStudioTarget). Любая неудача —
   исключение (owner_changed, source_changed, invalid_result, aborted): ничего не передано, подсказку пишет экран. Родные хвосты
   (PhotoOrderResult, VideoOrderResult, voice-transfer.ts) остаются как есть. */
import { endpoints } from '../../../api';
import type { SpeechOrder } from '../../../api/speech';
import { stagePhotoResult, type PhotoResultEntry } from '../../photo/photo-result-entry';
import { stageVideoResult } from '../../video/video-result-entry';
import { stageVoiceResult, type VoiceResultUse } from '../../voice/voice-result-entry';
import { openStudioTarget, type StudioTarget } from '../studio-navigation';

export interface HandoffSource {
  /** Владелец заказа на экране (svc.get().identity.owner) */
  ownerId: number | undefined;
  /** Заказ на экране */
  order: SpeechOrder;
  signal?: AbortSignal;
}
type Asset = NonNullable<SpeechOrder['result']>['assets'][number];

const aborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw new Error('aborted');
};
/** Тот же владелец (GET /app/me); иначе owner_changed */
export async function sameOwner(ownerId: number | undefined, signal?: AbortSignal): Promise<number> {
  const me = await endpoints.me();
  aborted(signal);
  if (typeof ownerId !== 'number' || me.user.id !== ownerId) throw new Error('owner_changed');
  return me.user.id;
}
/** Свежий заказ (GET /app/orders/{id}): готов, результат доступен, та же ревизия, что на экране; иначе source_changed */
async function freshOrder(order: SpeechOrder, signal?: AbortSignal): Promise<SpeechOrder> {
  const fresh = await endpoints.speech.get(order.id, signal);
  aborted(signal);
  if (fresh.id !== order.id || fresh.status !== 'done' || !fresh.result?.available || fresh.result_revision !== order.result_revision)
    throw new Error('source_changed');
  return fresh;
}
/** Владелец и свежий заказ; иначе owner_changed / source_changed */
export async function freshResult(src: HandoffSource): Promise<{ owner: number; order: SpeechOrder }> {
  const owner = await sameOwner(src.ownerId, src.signal);
  return { owner, order: await freshOrder(src.order, src.signal) };
}
/** Файл итога индекса свежего заказа: Blob, уже загруженный с этого заказа и ревизии, или новая авторизованная выгрузка; тип — тот, что нужен */
async function resultFile(
  order: SpeechOrder,
  asset: Asset,
  o: { type(mime: string): boolean; name: string; asIs?: boolean; blob?: Blob; signal?: AbortSignal },
): Promise<File> {
  const blob = o.blob ?? (await endpoints.speech.result(order, asset.index, o.signal));
  aborted(o.signal);
  if (!blob.size || !o.type(blob.type)) throw new Error('invalid_result');
  return new File([blob], o.asIs ? o.name : asset.file_name || o.name, { type: blob.type });
}
const assetAt = (order: SpeechOrder, index: number, type: (mime: string) => boolean) => {
  const asset = order.result?.assets.find(item => item.index === index);
  if (!asset || !type(asset.mime)) throw new Error('source_changed');
  return asset;
};
const image = (mime: string) => /^image\/(jpeg|png|webp)$/.test(mime);

/** Кадр итога (и ещё кадры того же заказа) — в «Рилс», «Видео», «Слайды»…: stagePhotoResult; open — куда перейти (по умолчанию — каталог,
    как у родного итога «Фото»). blobs — уже загруженные кадры этого заказа и ревизии (галерея хвоста): без новой выгрузки */
export async function handoffPhoto(
  o: HandoffSource & {
    index: number;
    extra?: number[];
    destination: PhotoResultEntry['destination'];
    blobs?: (index: number) => Blob | undefined;
    open?: StudioTarget;
  },
): Promise<void> {
  const { owner, order } = await freshResult(o);
  const take = async (index: number) => ({
    index,
    file: await resultFile(order, assetAt(order, index, image), { type: image, name: `steos-${index + 1}.jpg`, blob: o.blobs?.(index), signal: o.signal }),
  });
  const first = await take(o.index);
  const extra = [];
  for (const index of new Set(o.extra ?? [])) if (index !== o.index) extra.push(await take(index));
  aborted(o.signal);
  stagePhotoResult(
    { owner, orderId: order.id, revision: order.result_revision, index: first.index, file: first.file, ...(extra.length ? { extra } : {}), destination: o.destination },
    o.open,
  );
}

/** Ролик итога (video/mp4) — в «Говорящий кружок» своим видео: stageVideoResult. target — маршрут перехода, который публикует сервер
    (video-result-target.ts videoResultTarget): appId экрана и формат */
export async function handoffVideo(
  o: HandoffSource & { speech: 'original' | 'text'; target(signal: AbortSignal): Promise<{ appId: number; format: string }> },
): Promise<void> {
  const request = new AbortController();
  const onAbort = () => request.abort();
  if (o.signal?.aborted) request.abort();
  else o.signal?.addEventListener('abort', onAbort, { once: true });
  try {
    const owner = await sameOwner(o.ownerId, request.signal);
    const target = await o.target(request.signal);
    aborted(request.signal);
    const order = await freshOrder(o.order, request.signal);
    const mp4 = (mime: string) => mime === 'video/mp4';
    const asset = order.result?.assets.find(item => mp4(item.mime));
    if (!asset) throw new Error('source_changed');
    const file = await resultFile(order, asset, { type: mp4, name: 'video.mp4', asIs: true, signal: request.signal });
    stageVideoResult({
      owner,
      orderId: order.id,
      revision: order.result_revision,
      index: asset.index,
      file,
      speech: o.speech,
      appId: target.appId,
      format: target.format,
    });
  } finally {
    o.signal?.removeEventListener('abort', onAbort);
  }
}

/** Запись итога (audio/*) — в «Говорящий кружок» («Кружок из аудио» или персонаж говорит записью): stageVoiceResult */
export async function handoffVoice(o: HandoffSource & { use: VoiceResultUse }): Promise<void> {
  const { owner, order } = await freshResult(o);
  const audio = (mime: string) => mime.startsWith('audio/');
  const asset = order.result?.assets.find(item => audio(item.mime));
  if (!asset) throw new Error('source_changed');
  const file = await resultFile(order, asset, { type: audio, name: 'steos-voice.mp3', signal: o.signal });
  stageVoiceResult({ owner, orderId: order.id, revision: order.result_revision, index: asset.index, file, use: o.use });
}

/** Переход «Что дальше» без файла («Слайды с преимуществами», «Видео товара»): только после сверки владельца */
export async function handoffTarget(o: { ownerId: number | undefined; target: StudioTarget; signal?: AbortSignal }): Promise<void> {
  await sameOwner(o.ownerId, o.signal);
  openStudioTarget(o.target);
}
